import { Injectable, OnDestroy } from '@angular/core';
import { ConfigurationService, JambonzVoiceService, TwilioVoiceService, VoiceService } from 'app/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, merge } from 'rxjs/operators';
import { CallStatus, Ticket, VoiceProvider } from '../../models';

@Injectable({
    providedIn: 'root',
})
export class VoiceProxyService implements OnDestroy, VoiceService {
    currentProvider: VoiceProvider;

    providerConfigSubscription: Subscription;

    supportedProviders: { key: VoiceProvider; providerInstance: VoiceService }[];

    constructor(
        configurationService: ConfigurationService,
        private twilioVoiceService: TwilioVoiceService,
        private jambonzVoiceService: JambonzVoiceService
    ) {
        this.providerConfigSubscription = combineLatest([
            configurationService.getConfiguration('voiceProvider'),
            configurationService.getTenantConfiguration('voiceProvider'),
        ])
            .pipe(
                map((result: [{ value: VoiceProvider }, { value: VoiceProvider }]) => {
                    return (result[1] && result[1].value) ?? (result[0] && result[0].value) ?? undefined;
                })
            )
            .subscribe((provider: VoiceProvider) => {
                if (provider && provider !== this.currentProvider) {
                    if (this.currentProvider) {
                        const currentProviderInstance = this.selectProvider().providerInstance;
                        if (!currentProviderInstance.hasCalls()) {
                            currentProviderInstance.destroy();
                        }
                    }

                    this.currentProvider = provider;
                    this.init();
                }
            });

        this.supportedProviders = [
            { key: 'jambonz', providerInstance: this.jambonzVoiceService },
            { key: 'twilio', providerInstance: this.twilioVoiceService },
        ];
    }

    init(): void {
        this.selectProvider().providerInstance.init();
    }

    destroy(): void {
        this.selectProvider().providerInstance.destroy();
    }

    deviceStatus(): Observable<CallStatus> {
        return this.selectProvider().providerInstance.deviceStatus();
    }

    connectionError(): Observable<{ ticket: Ticket }> {
        return this.jambonzVoiceService.connectionError().pipe(merge(this.twilioVoiceService.connectionError()));
    }

    ngOnDestroy(): void {
        this.providerConfigSubscription.unsubscribe();
    }

    registerTicket(ticketId: string, conferenceId: string): void {
        this.selectProvider(ticketId).providerInstance.registerTicket(ticketId, conferenceId);
    }

    clearTicket(ticketId: string): void {
        const selectedProvider = this.selectProvider(ticketId);
        selectedProvider.providerInstance.clearTicket(ticketId);

        if (selectedProvider.key !== this.currentProvider && !selectedProvider.providerInstance.hasCalls()) {
            selectedProvider.providerInstance.destroy();
        }
    }

    hasCall(ticketId: string): boolean {
        return this.selectProvider(ticketId).providerInstance.hasCall(ticketId);
    }

    hasCalls(): boolean {
        return this.selectProvider().providerInstance.hasCalls();
    }

    pickUpCall(ticket: Ticket): void {
        this.selectProvider(ticket.id).providerInstance.pickUpCall(ticket);
    }

    hasConnection(ticketId: string): boolean {
        return this.selectProvider(ticketId).providerInstance.hasConnection(ticketId);
    }

    isMuted(ticketId: string): boolean {
        return this.selectProvider(ticketId).providerInstance.isMuted(ticketId);
    }

    isOnHold(ticketId: string): boolean {
        return this.selectProvider(ticketId).providerInstance.isOnHold(ticketId);
    }

    disconnect(ticketId: string, disconnectCallback?: () => void): void {
        this.selectProvider(ticketId).providerInstance.disconnect(ticketId, disconnectCallback);
    }

    mute(ticketId: string): void {
        this.selectProvider(ticketId).providerInstance.mute(ticketId);
    }

    pause(ticketId: string): void {
        this.selectProvider(ticketId).providerInstance.pause(ticketId);
    }

    async transfer(ticketId: string, forwardTo: string): Promise<void> {
        return this.selectProvider(ticketId).providerInstance.transfer(ticketId, forwardTo);
    }

    async createCall(originNumber: string, destinationNumber: string, ticketId: string): Promise<any> {
        return this.selectProvider(ticketId).providerInstance.createCall(originNumber, destinationNumber, ticketId);
    }

    private selectProvider(ticketId?: string): { key: VoiceProvider; providerInstance: VoiceService } {
        let selectedVoiceProvider: { key: VoiceProvider; providerInstance: VoiceService };

        // First check if the ticket already belongs to a provider.
        if (ticketId) {
            selectedVoiceProvider = this.supportedProviders.find((provider) => provider.providerInstance.hasCall(ticketId));
        }

        if (!selectedVoiceProvider) {
            const configProvider = this.supportedProviders.find((provider) => provider.key === this.currentProvider);
            if (!configProvider) {
                throw new Error(`Provider ${this.currentProvider} does not have an implementation`);
            }
            return configProvider;
        }

        return selectedVoiceProvider;
    }
}
